import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';
import { resolveNotificationNavigationPath } from '@/lib/navigation/notificationNavigation';
import { playOutbidFeedback } from '@/lib/alerts/alertFeedback';

const TOKEN_STORAGE_KEY = 'bilenia_native_push_token';
const PENDING_CELEBRATION_KEY = 'bilenia.pendingCelebration';

type PermissionState = 'prompt' | 'granted' | 'denied' | 'prompt-with-rationale';

type FirebaseMessagingPlugin = {
  requestPermissions: () => Promise<{ receive: string }>;
  checkPermissions: () => Promise<{ receive: string }>;
  getToken: () => Promise<{ token: string }>;
  deleteToken: () => Promise<void>;
  addListener: (
    event: string,
    callback: (event: any) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
};

/**
 * Registered by name so we do NOT import `@capacitor-firebase/messaging`
 * until that package is installed + native-synced. Without it, calls fail
 * gracefully and web/desktop never breaks on a missing module.
 */
const FirebaseMessaging = registerPlugin<FirebaseMessagingPlugin>(
  'FirebaseMessaging',
);

let listenersAttached = false;
let navigateHandler: ((path: string) => void) | null = null;
let foregroundHandler:
  | ((payload: {
      type?: string;
      title?: string;
      body?: string;
      data?: Record<string, unknown>;
    }) => void)
  | null = null;
let pluginUsable: boolean | null = null;

export type PendingCelebration = {
  kind: 'purchase' | 'auction_won' | 'generic';
  title?: string;
  body?: string;
  listingTitle?: string;
};

function listingTitleFromData(data?: Record<string, unknown>): string | undefined {
  if (!data) return undefined;
  const title =
    typeof data.listingTitle === 'string' ? data.listingTitle.trim() : '';
  const reg = typeof data.regNumber === 'string' ? data.regNumber.trim() : '';
  if (title && reg && !title.includes(reg)) return `${title} (${reg})`;
  return title || reg || undefined;
}

export function stashPendingCelebration(payload: PendingCelebration): void {
  try {
    localStorage.setItem(PENDING_CELEBRATION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function consumePendingCelebration(): PendingCelebration | null {
  try {
    const raw = localStorage.getItem(PENDING_CELEBRATION_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_CELEBRATION_KEY);
    return JSON.parse(raw) as PendingCelebration;
  } catch {
    return null;
  }
}

function celebrationFromType(type?: string): PendingCelebration | null {
  if (type === 'AUCTION_WON') return { kind: 'auction_won' };
  if (type === 'PURCHASE_CONFIRMED') return { kind: 'purchase' };
  return null;
}

function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

async function isMessagingPluginAvailable(): Promise<boolean> {
  if (!isNativeCapacitorApp()) return false;
  if (pluginUsable !== null) return pluginUsable;
  try {
    await FirebaseMessaging.checkPermissions();
    pluginUsable = true;
  } catch {
    // Native implementation missing until @capacitor-firebase/messaging is installed + cap sync.
    pluginUsable = false;
  }
  return pluginUsable;
}

async function registerTokenWithBackend(token: string): Promise<void> {
  const platform = Capacitor.getPlatform();
  if (platform !== 'ios' && platform !== 'android') return;

  await api.post('/devices/push-token', {
    token,
    platform,
  });
  storeToken(token);
}

/**
 * Soft-prompt flow: call only after user taps "Aktivera notiser".
 */
export async function requestNativePushPermissionAndRegister(): Promise<{
  granted: boolean;
  permission: PermissionState | 'unavailable';
}> {
  if (!(await isMessagingPluginAvailable())) {
    return { granted: false, permission: 'unavailable' };
  }

  try {
    const permission = await FirebaseMessaging.requestPermissions();
    const receive = (permission.receive || 'denied') as PermissionState;
    if (receive !== 'granted') {
      return { granted: false, permission: receive };
    }

    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      await registerTokenWithBackend(token);
    }
    return { granted: true, permission: receive };
  } catch {
    return { granted: false, permission: 'unavailable' };
  }
}

/** Refresh FCM token if OS permission already granted (no dialog). */
export async function syncNativePushTokenIfPermitted(): Promise<void> {
  if (!(await isMessagingPluginAvailable())) return;

  try {
    const check = await FirebaseMessaging.checkPermissions();
    if (check.receive !== 'granted') return;
    const { token } = await FirebaseMessaging.getToken();
    if (token) await registerTokenWithBackend(token);
  } catch (err) {
    console.warn('[push] syncNativePushTokenIfPermitted failed', err);
  }
}

export async function getNativePushPermissionState(): Promise<
  PermissionState | 'unavailable'
> {
  if (!(await isMessagingPluginAvailable())) return 'unavailable';
  try {
    const check = await FirebaseMessaging.checkPermissions();
    return (check.receive || 'prompt') as PermissionState;
  } catch {
    return 'unavailable';
  }
}

/** Unregister current device token (call on logout). */
export async function unregisterNativePushToken(): Promise<void> {
  const token = getStoredToken();

  try {
    if (token) {
      await api.delete('/devices/push-token', { data: { token } }).catch(() => undefined);
      storeToken(null);
    }
  } catch {
    /* ignore */
  }

  if (!(await isMessagingPluginAvailable())) return;
  try {
    await FirebaseMessaging.deleteToken();
  } catch {
    /* ignore */
  }
}

/**
 * Optional foreground handler (toast / celebration). Set from React before or after init.
 */
export function setNativePushForegroundHandler(
  handler:
    | ((payload: {
        type?: string;
        title?: string;
        body?: string;
        data?: Record<string, unknown>;
      }) => void)
    | null,
): void {
  foregroundHandler = handler;
}

/** Attach token refresh + notification tap / foreground listeners once. */
export async function initNativePushListeners(
  navigate: (path: string) => void,
): Promise<void> {
  if (!isNativeCapacitorApp()) return;
  navigateHandler = navigate;

  if (!(await isMessagingPluginAvailable()) || listenersAttached) return;
  listenersAttached = true;

  await FirebaseMessaging.addListener(
    'tokenReceived',
    async ({ token }: { token: string }) => {
      try {
        await registerTokenWithBackend(token);
      } catch (err) {
        console.warn('[push] tokenReceived register failed', err);
      }
    },
  );

  await FirebaseMessaging.addListener('notificationReceived', (event: any) => {
    const notification = event?.notification || {};
    const data = (notification.data || {}) as Record<string, unknown>;
    const type =
      typeof data.type === 'string'
        ? data.type
        : typeof data.event === 'string'
          ? data.event
          : undefined;
    const title =
      typeof notification.title === 'string'
        ? notification.title
        : typeof data.title === 'string'
          ? data.title
          : undefined;
    const body =
      typeof notification.body === 'string'
        ? notification.body
        : typeof data.body === 'string'
          ? data.body
          : undefined;

    if (type === 'BID_OUTBID') {
      playOutbidFeedback();
    }

    const celebration = celebrationFromType(type);
    if (celebration) {
      stashPendingCelebration({
        ...celebration,
        title,
        body,
        listingTitle: listingTitleFromData(data),
      });
    }

    foregroundHandler?.({ type, title, body, data });
  });

  await FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
    const data = (event.notification?.data || {}) as Record<string, unknown>;
    const type =
      typeof data.type === 'string'
        ? data.type
        : typeof data.event === 'string'
          ? data.event
          : undefined;
    if (type === 'BID_OUTBID') {
      playOutbidFeedback();
    }
    const celebration = celebrationFromType(type);
    if (celebration) {
      stashPendingCelebration({
        ...celebration,
        title:
          typeof event.notification?.title === 'string'
            ? event.notification.title
            : undefined,
        body:
          typeof event.notification?.body === 'string'
            ? event.notification.body
            : undefined,
        listingTitle: listingTitleFromData(data),
      });
    }
    const route =
      typeof data.route === 'string'
        ? data.route
        : typeof data.type === 'string'
          ? '/notifications'
          : null;
    const path = route
      ? resolveNotificationNavigationPath(route, data) || route
      : resolveNotificationNavigationPath('/notifications', data);
    if (path && navigateHandler) {
      navigateHandler(path);
    }
  });
}

export function isNativePushAvailable(): boolean {
  return isNativeCapacitorApp();
}
